import React from 'react';
import {
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Fab,
  useTheme,
  useMediaQuery,
  Typography
} from '@mui/material';
import {
  Users,
  Folders,
  Lightning,
  FileText,
  Stop,
  Pause,
  Play,
  FloppyDisk,
  X,
  SpeakerLow,
  PlusCircle,
  Microphone,
  Files
} from '@phosphor-icons/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppUser } from '@/contexts/AppUserProvider';

interface NavigationItem {
  label: string;
  icon: React.ReactNode;
  path?: string;
  action?: () => void;
  iconType: string;
}

interface RecordingItem {
  label: string;
  icon: React.ReactNode;
  action: (() => void) | null;
  color?: string;
}

interface MobileBottomNavProps {
  // Note: Recording state props kept for potential future use, but not currently used for styling
  isRecording?: boolean;
  isPaused?: boolean;
  showFloatingRecording?: boolean;
  onShowRecordingContainer?: () => void;
  guardedNavigate?: (path: string) => boolean;
}

export const MobileBottomNav: React.FC<MobileBottomNavProps> = ({
  isRecording = false,
  isPaused = false,
  showFloatingRecording = false,
  onShowRecordingContainer,
  guardedNavigate
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { appUser } = useAppUser();

  if (!isMobile) return null;


  // Helper function to get icon with proper weight based on selection
  const getIcon = (iconType: string, isSelected: boolean) => {
    const weight = isSelected ? 'fill' : 'regular';
    const size = 24;
    
    switch (iconType) {
      case 'people':
        return <Users size={size} weight={weight} />;
      case 'collections':
        return <Folders size={size} weight={weight} />;
      case 'records':
        return <Files size={size} weight={weight} />;
      case 'create':
        return <PlusCircle size={size} weight={weight} />;
      case 'record':
        return <Microphone size={size} weight={weight} />;
      case 'upload':
        return <PlusCircle size={size} weight={weight} />;
      case 'templates':
        return <FileText size={size} weight={weight} />;
      case 'ai-chat':
        return <Lightning size={size} weight={weight} />;
      default:
        return <Users size={size} weight={weight} />;
    }
  };

  // Navigation items ordered: Collections, Files, Ask (centered), Upload, Record
  // Ask is always in the middle for all users
  const aiChatItem: NavigationItem = {
    label: 'Ask',
    icon: <Lightning size={24} weight="light" />,
    iconType: 'ai-chat',
    path: '/ai-chat'
  };

  const leftItems: NavigationItem[] = [
    { label: 'Folders', icon: <Folders size={24} />, iconType: 'collections', path: '/collections' },
    { label: 'Files', icon: <Files size={24} />, iconType: 'records', path: '/records' }
  ];

  const rightItems: NavigationItem[] = [
    { label: 'Upload', icon: <PlusCircle size={24} />, iconType: 'upload', path: '/create/upload' },
    { label: 'Record', icon: <Microphone size={24} />, iconType: 'record', path: '/create/record' }
  ];

  // Combine: left items, Ask (centered), right items
  const navigationItems: NavigationItem[] = [
    ...leftItems,
    aiChatItem,
    ...rightItems
  ];

  const handleNavigation = (path: string) => {
    if (path) {
      // Use guarded navigation if available, otherwise fall back to regular navigation
      if (guardedNavigate) {
        guardedNavigate(path);
      } else {
        navigate(path);
      }
    }
  };

  const handleItemClick = (index: number) => {
    const item = navigationItems[index];
    if (item) {
      // Special handling for create button when recording is already running
      if (item.iconType === 'create' && isRecording && onShowRecordingContainer) {
        onShowRecordingContainer();
      } else if ('action' in item && item.action) {
        item.action();
      } else if ('path' in item && item.path) {
        handleNavigation(item.path);
      }
    }
  };

  const getCurrentValue = () => {
    const currentPath = location.pathname;
    const matchingItem = navigationItems.find(item => 
      ('path' in item && item.path === currentPath) || 
      currentPath === '/ai-chat' && item.path === '/ai-chat'
    );
    return matchingItem ? navigationItems.indexOf(matchingItem) : -1;
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        paddingBottom: 'env(safe-area-inset-bottom)' // Handle iPhone home indicator
      }}
    >
      <BottomNavigation
        value={getCurrentValue()}
        onChange={(event, newValue) => {
          handleItemClick(newValue);
        }}
        sx={{
          height: 64,
          backgroundColor: theme.palette.background.paper, // Keep white background always
          transition: 'background-color 0.3s ease',
          '& .MuiBottomNavigationAction-root': {
            minWidth: 'auto',
            padding: '12px 16px 8px', // More top padding since no labels
            '&.Mui-selected': {
              color: theme.palette.primary.main,
              backgroundColor: 'transparent !important', // Remove any background fill
              '&::before': {
                display: 'none' // Remove any pseudo-element backgrounds
              },
              '&::after': {
                display: 'none' // Remove any pseudo-element backgrounds
              }
            },
            '&:hover': {
              backgroundColor: 'transparent !important' // Remove hover backgrounds too
            },
            '& .MuiBottomNavigationAction-label': {
              display: 'none' // Hide labels, show icons only
            }
          }
        }}
      >
        {navigationItems.map((item, index) => {
          const hasAction = ('path' in item && item.path) || ('action' in item && item.action);
          const isSelected = getCurrentValue() === index;
          
          // Use dynamic icon for navigation items based on selection state
          const dynamicIcon = getIcon(item.iconType, isSelected);
          
          return (
            <BottomNavigationAction
              key={index}
              label={item.label}
              icon={dynamicIcon}
              disableRipple
              sx={{
                opacity: hasAction ? 1 : 0,
                pointerEvents: hasAction ? 'auto' : 'none',
                backgroundColor: 'transparent !important',
                '&:hover': {
                  backgroundColor: 'transparent !important'
                },
                '&::before, &::after': {
                  display: 'none'
                },
                '& .MuiTouchRipple-root': {
                  display: 'none'
                }
              }}
            />
          );
        })}
      </BottomNavigation>

    </Box>
  );
};
